@if (auth.isLoggedIn) {
  <button type="button" class="chat-launcher" (click)="toggleOpen()">
    {{ open() ? '×' : ('Help_chat_launcher_tr' | translate) }}
  </button>
  @if (open()) {
    <section class="chat-panel" cdkTrapFocus [cdkTrapFocusAutoCapture]="true">
      <header class="chat-header">
        <div>
          <span class="chat-kicker">SupplyCanvas</span>
          <strong>{{ 'Help_chat_header_tr' | translate }}</strong>
        </div>
        <button type="button" class="reset-session-button" (click)="resetSession()" [attr.aria-label]="'Help_chat_reset_aria_tr' | translate" [attr.title]="'Help_chat_reset_hint_tr' | translate">
          <span class="reset-session-icon" aria-hidden="true">delete_sweep</span>
        </button>
      </header>
      @if (previousSession()) {
        <div class="prev-session-banner">
          <span>{{ 'Help_chat_prev_session_banner_tr' | translate }}</span>
          <button type="button" (click)="switchToPreviousSession()">
            {{ 'Help_chat_switch_back_tr' | translate }}
          </button>
          <button type="button" class="dismiss" (click)="dismissPreviousBanner()" aria-label="dismiss">×</button>
        </div>
      }
      @if (notice()) {
        <div class="chat-notice" role="status">
          <span>{{ notice() }}</span>
          <button type="button" (click)="retryInitialize()">{{ 'Help_chat_retry_tr' | translate }}</button>
        </div>
      }
      <div class="chat-body">
        @if (connectionState() !== 'connected') {
          <div class="chat-connection-status" data-testid="help-chat-connection-status" role="status">
            {{ connectionState() === 'connecting' ? ('Help_chat_connecting_tr' | translate) : ('Help_chat_reconnecting_tr' | translate) }}
          </div>
        }
        <div class="messages" #messagesContainer aria-live="polite" (scroll)="markMessagesRead()" tabindex="0">
          @if (newMessageCount()) {
            <button type="button" class="new-messages-button" (click)="scrollMessages('smooth'); markMessagesRead()">
              {{ newMessageCount() }} new message{{ newMessageCount() === 1 ? '' : 's' }}
            </button>
          }
          @if (hasOlderMessages()) {
            <button
              type="button"
              class="load-history-button"
              [disabled]="loadingOlder()"
              (click)="loadOlderMessages()"
            >
              {{ loadingOlder() ? ('Help_chat_loading_older_tr' | translate) : ('Help_chat_load_older_tr' | translate) }}
            </button>
          }
          @if (loading() && !messages().length) {
            <div class="chat-skeleton" data-testid="help-chat-skeleton" aria-label="Loading chat" role="status">
              <span class="skeleton-line skeleton-line--short"></span>
              <span class="skeleton-line"></span>
              <span class="skeleton-line skeleton-line--medium"></span>
            </div>
          } @else if (!messages().length) {
            <div class="empty-state" role="status" [attr.aria-label]="'Help_chat_empty_state_tr' | translate">
              <span class="empty-state__mark" aria-hidden="true">
                <img src="assets/empty_chat.png" alt="" />
              </span>
              <span class="sr-only">{{ 'Help_chat_empty_state_tr' | translate }}</span>
            </div>
          }
          @for (message of messages(); track $index) {
            @if (shouldShowDay(message, $index)) {
              <div class="message-day" role="separator">{{ messageDayLabel(message) }}</div>
            }
            <article
              class="message"
              [class.message-user]="message.role === 'user'"
              [class.message-ai]="message.role === 'ai'"
            >
              <div class="message-label">{{ message.role === 'user' ? ('Help_chat_you_tr' | translate) : ('Help_chat_assistant_label_tr' | translate) }}</div>
              <div class="message-content">
                @for (part of messageParts(message); track $index) {
                  @if (part.text) {
                    @for (textPart of formattedTextParts(part.text); track $index) {
                      @if (textPart.horizontalRule) {
                        <hr class="message-text--rule" />
                      } @else if (textPart.listItem) {
                        <span class="message-text message-text--list-item"><span class="message-text--bullet" aria-hidden="true">•</span><span>
                          @for (listPart of formattedTextParts(textPart.text || ''); track $index) {
                            @if (listPart.bold) { <strong class="message-text--bold">{{ listPart.text }}</strong> }
                            @else if (listPart.italic) { <em class="message-text--italic">{{ listPart.text }}</em> }
                            @else if (listPart.code) { <code class="message-text--code">{{ listPart.text }}</code> }
                            @else if (listPart.linkUrl) { <a class="message-text--link" [href]="listPart.linkUrl" target="supplycanvas-source" rel="noopener noreferrer">{{ listPart.text }}</a> }
                            @else { {{ listPart.text }} }
                          }
                        </span></span>
                      } @else if (textPart.headingLevel === 2) {
                        <h2 class="message-text message-text--heading">{{ textPart.text }}</h2>
                      } @else if (textPart.headingLevel === 3) {
                        <h3 class="message-text message-text--heading">{{ textPart.text }}</h3>
                      } @else if (textPart.headingLevel === 4) {
                        <h4 class="message-text message-text--heading">{{ textPart.text }}</h4>
                      } @else if (textPart.bold) {
                        <strong class="message-text message-text--bold">{{ textPart.text }}</strong>
                      } @else if (textPart.italic) {
                        <em class="message-text message-text--italic">{{ textPart.text }}</em>
                      } @else if (textPart.code) {
                        <code class="message-text message-text--code">{{ textPart.text }}</code>
                      } @else if (textPart.linkUrl) {
                        <a class="message-text message-text--link" [href]="textPart.linkUrl" target="supplycanvas-source" rel="noopener noreferrer">{{ textPart.text }}</a>
                      } @else {
                        <span class="message-text">{{ textPart.text }}</span>
                      }
                    }
                  }
                  @if (part.action; as action) {
                    <button
                      type="button"
                      class="sc-action-chip"
                      [class.sc-action-chip--support]="action.category === 'support'"
                      [class.sc-action-chip--account]="action.category === 'account'"
                      [class.sc-action-chip--navigation]="action.category === 'navigation'"
                      [disabled]="action.used"
                      [class.sc-action-chip--used]="action.used"
                      (click)="actionButtonClick(action, $event)"
                    >
                      <span
                        class="sc-action-chip__icon"
                        [class.sc-action-chip__icon--support]="action.category === 'support'"
                        [class.sc-action-chip__icon--account]="action.category === 'account'"
                        [class.sc-action-chip__icon--navigation]="action.category === 'navigation'"
                        aria-hidden="true"
                      >{{
                        action.category === 'support' ? '✉' : action.category === 'account' ? '🔑' : '→'
                      }}</span>
                      <span>{{ action.label }}</span>
                    </button>
                  }
                }
                @if (message.sources?.length) {
                  <div class="message-sources">
                    <span class="message-sources__label">{{ 'Help_chat_sources_tr' | translate }}</span>
                    @for (source of message.sources; track source.path) {
                      <a class="message-source" [href]="source.url" target="_blank" rel="noopener noreferrer" (click)="openCitation(source.url, $event)">{{ source.path }}</a>
                    }
                  </div>
                }
              </div>
              @if (!message.streaming && messageTime(message)) {
                <time class="message-time" [attr.datetime]="message.createdAt">{{ messageTime(message) }}</time>
              }
              @if (message.streaming) {
                <span class="streaming-dots">●●●</span>
              }
              @if (message.failed) {
                <button type="button" class="retry-message-button" (click)="retryMessage($index)">{{ 'Help_chat_retry_tr' | translate }}</button>
              }
            </article>
          }
        </div>
        <form class="composer" (submit)="sendMessage($event)">
          <textarea
            #messageInput
            [(ngModel)]="draft"
            name="message"
            rows="1"
            [placeholder]="'Help_chat_placeholder_tr' | translate"
            [disabled]="sending()"
            (input)="resizeComposer()"
            (keydown.enter)="handleComposerKeydown($event)"
          ></textarea>
          <div class="composer-footer">
            <span class="composer-hint">{{
              sending() ? ('Help_chat_generating_tr' | translate) : ('Help_chat_composer_hint_tr' | translate)
            }}</span>
            @if (sending()) {
              <button type="button" class="stop-button" (click)="stopGenerating()">■</button>
            } @else {
              <button type="submit" class="send-button" [disabled]="!draft.trim()">↑</button>
            }
          </div>
        </form>
        @if (loading()) {
          <div class="chat-loading">{{ 'Help_chat_loading_tr' | translate }}</div>
        }
      </div>
    </section>
  }
}
